import { useState, useRef, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../utils/api'
import {
  ArrowLeft, Paperclip, Send, Clock, User, Tag, Layers,
  AlertCircle, CheckCircle, XCircle, ChevronDown, Edit3,
  Lock, Unlock, Building2, Plus, Trash2, Square, CheckSquare,
  Timer, BarChart2, History, ListTodo, ClipboardList, ShieldAlert, UserCheck,
  Printer, Upload, RefreshCw,
} from 'lucide-react'
import SignaturePad, { SignaturePadHandle } from '../components/SignaturePad'
import { PRIORITY_CFG, STATUS_CFG, TYPE_CFG, SLABadge, ResponseSLABadge, UserSearchSelect, AssetSearchSelect, TicketPriority, TicketStatus, TicketType } from './TicketsPage'
import { io } from 'socket.io-client'
import RichTextEditor from '../components/RichTextEditor'
import { useAuth } from '../hooks/useAuth'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Avatar({ name, size = 'md' }: { name?: string; size?: 'sm' | 'md' }) {
  const initials = (name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  const cls = size === 'sm' ? 'w-6 h-6 text-xs' : 'w-8 h-8 text-sm'
  return <div className={`${cls} rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold flex-shrink-0`}>{initials}</div>
}

function FileIcon({ mime }: { mime?: string }) {
  if (mime?.startsWith('image/')) return <span className="text-blue-500">🖼</span>
  if (mime?.includes('pdf'))       return <span className="text-red-500">📄</span>
  return <span className="text-gray-400">📎</span>
}

function fmtDate(d?: string) {
  if (!d) return ''
  return new Date(d).toLocaleString('th-TH-u-ca-gregory', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' })
}

function fmtMinutes(m: number) {
  if (m < 60) return `${m} นาที`
  const h = Math.floor(m / 60), min = m % 60
  return min ? `${h} ชม. ${min} นาที` : `${h} ชม.`
}

// ─── Status Change Dropdown ───────────────────────────────────────────────────

const STATUS_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  OPEN:        ['ASSIGNED', 'IN_PROGRESS', 'PENDING', 'CANCELLED'],
  ASSIGNED:    ['IN_PROGRESS', 'PENDING', 'CANCELLED'],
  IN_PROGRESS: ['PENDING', 'RESOLVED', 'CANCELLED'],
  PENDING:     ['IN_PROGRESS', 'RESOLVED', 'CANCELLED'],
  RESOLVED:    ['CLOSED', 'IN_PROGRESS'],
  CLOSED:      [],
  CANCELLED:   [],
}

function StatusDropdown({ current, onChange }: { current: TicketStatus; onChange: (s: TicketStatus, comment?: string) => void }) {
  const [open, setOpen] = useState(false)
  const [onholdModal, setOnholdModal] = useState(false)
  const [onholdComment, setOnholdComment] = useState('')
  const transitions = STATUS_TRANSITIONS[current] || []

  const handleSelect = (s: TicketStatus) => {
    setOpen(false)
    if (s === 'PENDING') { setOnholdModal(true); return }
    onChange(s)
  }

  const confirmOnhold = () => {
    if (!onholdComment.trim()) return
    onChange('PENDING', onholdComment.trim())
    setOnholdModal(false)
    setOnholdComment('')
  }

  const statusDot: Record<string, string> = {
    RESOLVED: 'bg-green-500', CLOSED: 'bg-gray-400', IN_PROGRESS: 'bg-purple-500',
    PENDING: 'bg-amber-500', ASSIGNED: 'bg-indigo-500', OPEN: 'bg-blue-500',
  }

  return (
    <>
      <div className="relative">
        <button onClick={() => setOpen(o => !o)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border ${STATUS_CFG[current].color}`}>
          {STATUS_CFG[current].label}
          {transitions.length > 0 && <ChevronDown size={13}/>}
        </button>
        {open && transitions.length > 0 && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setOpen(false)}/>
            <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-40 py-1 overflow-hidden">
              {transitions.map(s => (
                <button key={s} onClick={() => handleSelect(s)}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full inline-block ${statusDot[s] || 'bg-gray-400'}`}/>
                  {STATUS_CFG[s].label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {onholdModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 space-y-4">
            <h2 className="text-base font-semibold text-gray-900">เปลี่ยนเป็น On Hold</h2>
            <p className="text-sm text-gray-500">กรุณาระบุเหตุผลที่พักการดำเนินงาน</p>
            <textarea
              value={onholdComment}
              onChange={e => setOnholdComment(e.target.value)}
              rows={4}
              placeholder="เหตุผลที่ On Hold..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
              autoFocus
            />
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setOnholdModal(false); setOnholdComment('') }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">ยกเลิก</button>
              <button onClick={confirmOnhold} disabled={!onholdComment.trim()}
                className="px-5 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-40">
                ยืนยัน On Hold
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Comment Box ──────────────────────────────────────────────────────────────

function CommentBox({ ticketId, onPosted }: { ticketId: string; onPosted: () => void }) {
  const [content, setContent]     = useState('')
  const [isInternal, setInternal] = useState(false)
  const [files, setFiles]         = useState<File[]>([])
  const [loading, setLoading]     = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const submit = async () => {
    if (!content.trim()) return
    setLoading(true)
    try {
      const fd = new FormData()
      fd.append('content', content.trim())
      fd.append('isInternal', String(isInternal))
      files.forEach(f => fd.append('files', f))
      await api.post(`/tickets/${ticketId}/comments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setContent(''); setFiles([]); setInternal(false)
      onPosted()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  return (
    <div className={`border rounded-xl overflow-hidden transition-colors ${isInternal ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white'}`}>
      <textarea value={content} onChange={e => setContent(e.target.value)} rows={3}
        className="w-full px-4 py-3 text-sm bg-transparent focus:outline-none resize-none"
        placeholder={isInternal ? 'หมายเหตุภายใน (ผู้แจ้งจะไม่เห็น)...' : 'เขียนข้อความตอบกลับ...'}
        onKeyDown={e => e.key === 'Enter' && e.metaKey && submit()}/>

      {files.length > 0 && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <span key={i} className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded-full">
              <Paperclip size={11}/> {f.name}
              <button onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500"><XCircle size={11}/></button>
            </span>
          ))}
        </div>
      )}

      <div className="px-3 py-2 border-t flex items-center justify-between gap-2" style={{ borderColor: isInternal ? '#fcd34d40' : '#f3f4f6' }}>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file" multiple className="hidden" onChange={e => setFiles(Array.from(e.target.files || []))}/>
          <button onClick={() => fileRef.current?.click()} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400" title="แนบไฟล์">
            <Paperclip size={16}/>
          </button>
          <button onClick={() => setInternal(v => !v)}
            className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border transition-colors ${isInternal ? 'bg-amber-100 text-amber-700 border-amber-300' : 'text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
            {isInternal ? <><Lock size={11}/> หมายเหตุภายใน</> : <><Unlock size={11}/> สาธารณะ</>}
          </button>
        </div>
        <button onClick={submit} disabled={loading || !content.trim()}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40">
          <Send size={14}/> {loading ? 'กำลังส่ง...' : 'ส่ง'}
        </button>
      </div>
    </div>
  )
}

// ─── Side Panel Field ─────────────────────────────────────────────────────────

function SideField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium text-gray-400 uppercase mb-1">{label}</p>
      <div className="text-sm text-gray-700">{children}</div>
    </div>
  )
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

function EditTicketModal({ ticket, categoryTree, onClose, onSaved }: { ticket: any; categoryTree: any[]; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    title:         ticket.title         || '',
    description:   ticket.description   || '',
    priority:      ticket.priority      || 'MEDIUM',
    type:          ticket.type          || 'INCIDENT',
    categoryId:    ticket.categoryId    || '',
    subcategoryId: ticket.subcategoryId || '',
    itemId:        ticket.itemId        || '',
    requesterId:   ticket.requesterId   || '',
    assigneeId:    ticket.assigneeId    || '',
    assetId:       ticket.assetId       || '',
    companyId:     ticket.companyId     || '',
  })
  const [loading, setLoading] = useState(false)

  const { data: assignees = [] } = useQuery<any[]>({ queryKey: ['users-assignable'], queryFn: () => api.get('/users/assignable').then(r => r.data) })
  const { data: members = [] }   = useQuery<any[]>({ queryKey: ['users-members'],    queryFn: () => api.get('/users/members').then(r => r.data) })
  const { data: companies = [] } = useQuery<any[]>({ queryKey: ['companies'],        queryFn: () => api.get('/masterdata/companies').then(r => r.data) })

  const selectedCat  = categoryTree.find((c: any) => c.id === form.categoryId)
  const subcategories: any[] = selectedCat?.children || []
  const selectedSub  = subcategories.find((s: any) => s.id === form.subcategoryId)
  const items: any[] = selectedSub?.children || []

  const handleCategoryChange = (id: string) => setForm(f => ({ ...f, categoryId: id, subcategoryId: '', itemId: '' }))
  const handleSubcatChange   = (id: string) => setForm(f => ({ ...f, subcategoryId: id, itemId: '' }))

  const save = async () => {
    setLoading(true)
    try {
      const payload: any = { ...form }
      if (!payload.requesterId)   payload.requesterId   = ''
      if (!payload.assigneeId)    payload.assigneeId    = ''
      if (!payload.assetId)       payload.assetId       = ''
      if (!payload.companyId)     payload.companyId     = ''
      if (!payload.subcategoryId) payload.subcategoryId = ''
      if (!payload.itemId)        payload.itemId        = ''
      await api.put(`/tickets/${ticket.id}`, payload)
      onSaved(); onClose()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setLoading(false) }
  }

  const sel = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">แก้ไข Ticket</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400"><XCircle size={17}/></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">หัวข้อ</label>
            <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">ประเภท</label>
              <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value}))} className={sel}>
                <option value="INCIDENT">Incident</option>
                <option value="SERVICE_REQUEST">Service Request</option>
                <option value="CHANGE">Change</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">ความสำคัญ</label>
              <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className={sel}>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 block">หมวดหมู่</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <p className="text-xs text-gray-400 mb-1">Category</p>
                <select value={form.categoryId} onChange={e => handleCategoryChange(e.target.value)} className={sel}>
                  <option value="">— ไม่ระบุ —</option>
                  {categoryTree.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Subcategory</p>
                <select value={form.subcategoryId} onChange={e => handleSubcatChange(e.target.value)} className={sel} disabled={!form.categoryId || subcategories.length === 0}>
                  <option value="">— ไม่ระบุ —</option>
                  {subcategories.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Item</p>
                <select value={form.itemId} onChange={e => setForm(f => ({...f, itemId: e.target.value}))} className={sel} disabled={!form.subcategoryId || items.length === 0}>
                  <option value="">— ไม่ระบุ —</option>
                  {items.map((i: any) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">บริษัท</label>
            <select value={form.companyId} onChange={e => setForm(f => ({...f, companyId: e.target.value}))} className={sel}>
              <option value="">— ไม่ระบุ —</option>
              {companies.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <AssetSearchSelect
              label="Asset ที่เกี่ยวข้อง"
              value={form.assetId}
              onChange={id => setForm(f => ({...f, assetId: id}))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <UserSearchSelect
                label="ผู้เปิด Ticket (ผู้แจ้ง)"
                value={form.requesterId}
                onChange={v => setForm(f => ({...f, requesterId: v}))}
                placeholder="ค้นหาผู้แจ้ง..."
                members={members}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">ผู้รับผิดชอบ</label>
              <select value={form.assigneeId} onChange={e => setForm(f => ({...f, assigneeId: e.target.value}))} className={sel}>
                <option value="">— ยังไม่ได้มอบหมาย —</option>
                {assignees.map((u: any) => <option key={u.id} value={u.id}>{u.displayName || u.username}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">รายละเอียด</label>
            <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">ยกเลิก</button>
          <button onClick={save} disabled={loading}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {loading ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Tab: Details (Properties + Conversation) ────────────────────────────────

function DetailsTab({ ticket, isClosed, refetch, isEditing, editForm, setEditForm, categoryTree, assignees, members, companies, isViewer, isSuperAdmin }: {
  ticket: any; isClosed: boolean; refetch: () => void;
  isEditing: boolean;
  editForm: any;
  setEditForm: React.Dispatch<React.SetStateAction<any>>;
  categoryTree: any[];
  assignees: any[];
  members: any[];
  companies: any[];
  isViewer: boolean;
  isSuperAdmin: boolean;
}) {
  const comments = ticket.comments || []
  const statConf = STATUS_CFG[ticket.status as TicketStatus]
  const prioConf = PRIORITY_CFG[ticket.priority as TicketPriority]
  const typeConf = TYPE_CFG[ticket.type as TicketType]

  const selectedCat   = categoryTree.find((c: any) => c.id === editForm?.categoryId)
  const subcategories: any[] = selectedCat?.children || []
  const selectedSub   = subcategories.find((s: any) => s.id === editForm?.subcategoryId)
  const items: any[]  = selectedSub?.children || []

  const sel = 'px-2 py-1.5 border border-blue-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 w-full'

  return (
    <div className="space-y-4">
      {/* Properties grid */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</p>
        </div>
        <div className="px-4 py-3 border-b border-gray-100">
          {isEditing ? (
            <RichTextEditor
              content={editForm.description || ''}
              onChange={html => setEditForm((f: any) => ({...f, description: html}))}
              placeholder="รายละเอียด... (แทรกรูปภาพหรือตารางได้)"
              uploadEndpoint="/tickets/upload-image"
            />
          ) : ticket.description ? (
            <div className="kb-content text-sm" style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: ticket.description }}/>
          ) : (
            <p className="text-sm text-gray-400 italic">ไม่มีรายละเอียด</p>
          )}
        </div>
        <div className="px-4 py-1">
          <table className="w-full">
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 w-40 align-top pt-2.5">Request Type</td>
                <td className="py-2 text-sm">
                  {isEditing ? (
                    <select value={editForm.type || ''} onChange={e => setEditForm((f: any) => ({...f, type: e.target.value}))} className={sel}>
                      <option value="INCIDENT">Incident</option>
                      <option value="SERVICE_REQUEST">Service Request</option>
                      <option value="PROBLEM">Problem</option>
                      <option value="CHANGE">Change</option>
                    </select>
                  ) : <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeConf?.color}`}>{typeConf?.label}</span>}
                </td>
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 w-36 align-top pt-2.5">Priority</td>
                <td className="py-2 text-sm">
                  {isEditing ? (
                    <select value={editForm.priority || ''} onChange={e => setEditForm((f: any) => ({...f, priority: e.target.value}))} className={sel}>
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="CRITICAL">Critical</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${prioConf?.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${prioConf?.dot}`}/>
                      {prioConf?.label}
                    </span>
                  )}
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Status</td>
                <td className="py-2 text-sm">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${statConf?.color}`}>{statConf?.label}</span>
                </td>
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Category</td>
                <td className="py-2 text-sm">
                  {isEditing ? (
                    <select value={editForm.categoryId || ''} onChange={e => setEditForm((f: any) => ({...f, categoryId: e.target.value, subcategoryId: '', itemId: ''}))} className={sel}>
                      <option value="">— ไม่ระบุ —</option>
                      {categoryTree.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  ) : <span className="text-gray-700">{ticket.category?.name || <span className="text-gray-400">—</span>}</span>}
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Requester</td>
                <td className="py-2 text-sm">
                  {isEditing ? (
                    <UserSearchSelect label="" value={editForm.requesterId || ''} onChange={v => setEditForm((f: any) => ({...f, requesterId: v}))} placeholder="ค้นหาผู้แจ้ง..." members={members}/>
                  ) : ticket.requester ? (
                    <div className="flex items-center gap-1.5">
                      <Avatar name={ticket.requester.displayName || ticket.requester.username} size="sm"/>
                      <span>{ticket.requester.displayName || ticket.requester.username}</span>
                    </div>
                  ) : <span className="text-gray-400">—</span>}
                </td>
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Subcategory</td>
                <td className="py-2 text-sm">
                  {isEditing ? (
                    <select value={editForm.subcategoryId || ''} onChange={e => setEditForm((f: any) => ({...f, subcategoryId: e.target.value, itemId: ''}))} className={sel} disabled={!editForm.categoryId || subcategories.length === 0}>
                      <option value="">— ไม่ระบุ —</option>
                      {subcategories.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  ) : <span className="text-gray-700">{ticket.subcategory?.name || <span className="text-gray-400">—</span>}</span>}
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Technician</td>
                <td className="py-2 text-sm">
                  {isEditing && !isViewer ? (
                    <UserSearchSelect label="" value={editForm.assigneeId || ''} onChange={v => setEditForm((f: any) => ({...f, assigneeId: v}))} placeholder="ค้นหาผู้รับผิดชอบ..." members={assignees}/>
                  ) : ticket.assignee ? (
                    <div className="flex items-center gap-1.5">
                      <Avatar name={ticket.assignee.displayName || ticket.assignee.username} size="sm"/>
                      <span>{ticket.assignee.displayName || ticket.assignee.username}</span>
                    </div>
                  ) : <span className="text-gray-400">Not Assigned</span>}
                </td>
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Item</td>
                <td className="py-2 text-sm">
                  {isEditing ? (
                    <select value={editForm.itemId || ''} onChange={e => setEditForm((f: any) => ({...f, itemId: e.target.value}))} className={sel} disabled={!editForm.subcategoryId || items.length === 0}>
                      <option value="">— ไม่ระบุ —</option>
                      {items.map((i: any) => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  ) : <span className="text-gray-700">{ticket.item?.name || <span className="text-gray-400">—</span>}</span>}
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Company</td>
                <td className="py-2 text-sm">
                  {isEditing ? (
                    <select value={editForm.companyId || ''} onChange={e => setEditForm((f: any) => ({...f, companyId: e.target.value}))} className={sel}>
                      <option value="">— ไม่ระบุ —</option>
                      {companies.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  ) : <span className="text-gray-700">{ticket.company?.name || <span className="text-gray-400">—</span>}</span>}
                </td>
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Asset</td>
                <td className="py-2 text-sm">
                  {isEditing ? (
                    <AssetSearchSelect label="" value={editForm.assetId || ''} onChange={id => setEditForm((f: any) => ({...f, assetId: id}))}/>
                  ) : ticket.asset ? (
                    <Link to={`/assets/${ticket.asset.id}`} className="text-blue-600 hover:underline">{ticket.asset.assetCode} — {ticket.asset.name}</Link>
                  ) : <span className="text-gray-400">—</span>}
                </td>
              </tr>
              <tr>
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Created Date</td>
                <td className="py-2 text-sm text-gray-700">
                  {isEditing && isSuperAdmin ? (
                    <input type="datetime-local" value={editForm.createdAt || ''}
                      onChange={e => setEditForm((f: any) => ({...f, createdAt: e.target.value}))} className={sel}/>
                  ) : fmtDate(ticket.createdAt)}
                </td>
                <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Due By</td>
                <td className="py-2 text-sm">
                  {ticket.dueDate ? <SLABadge dueDate={ticket.dueDate} status={ticket.status}/> : <span className="text-gray-400">—</span>}
                </td>
              </tr>
              {!ticket.assigneeId && ticket.responseDueDate && (
                <tr className="border-t border-gray-100">
                  <td className="py-2 pr-4 text-xs font-medium text-gray-500 align-top pt-2.5">Respond By</td>
                  <td className="py-2 text-sm" colSpan={3}>
                    <ResponseSLABadge responseDueDate={ticket.responseDueDate} hasAssignee={!!ticket.assigneeId}/>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Attachments */}
      <TicketAttachmentsSection ticketId={ticket.id} initial={ticket.attachments || []} readOnly={isClosed} onSaved={refetch}/>

      {/* Conversations */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Conversations</p>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <input type="checkbox" defaultChecked className="rounded"/> E-mail
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <input type="checkbox" className="rounded"/> System Notifications
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
            <input type="checkbox" defaultChecked className="rounded"/> Notes
          </label>
        </div>
        <div className="p-4 space-y-3">
          {comments.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-4">No Conversations</p>
          )}
          {comments.map((c: any) => (
            <div key={c.id} className={`rounded-xl p-4 border ${c.isInternal ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-start gap-3">
                <Avatar name={c.user?.displayName || c.user?.username}/>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-800">{c.user?.displayName || c.user?.username || 'ผู้ใช้'}</span>
                    {c.isInternal && <span className="text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded flex items-center gap-0.5"><Lock size={10}/> Notes</span>}
                    <span className="text-xs text-gray-400 ml-auto">{fmtDate(c.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{c.content}</p>
                  {c.attachments?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {c.attachments.map((a: any) => (
                        <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full hover:bg-blue-100 border border-blue-100">
                          <FileIcon mime={a.mimeType}/> {a.originalName}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {!isClosed && <CommentBox ticketId={ticket.id} onPosted={refetch}/>}
          {isClosed && (
            <div className="text-center py-3 text-sm text-gray-400 bg-gray-50 rounded-xl border border-gray-100">
              Ticket นี้ถูก{statConf.label}แล้ว — ไม่สามารถเพิ่มความคิดเห็นได้
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Tab: Resolution ──────────────────────────────────────────────────────────

function ResolutionTab({ ticket, refetch, onStatusChange }: { ticket: any; refetch: () => void; onStatusChange: (s: TicketStatus, comment?: string) => void }) {
  const [text, setText] = useState(ticket.resolution || '')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [addWorkLog, setAddWorkLog] = useState(false)
  const [wlHours, setWlHours] = useState('')
  const [wlMins, setWlMins] = useState('')
  const [wlNote, setWlNote] = useState('')
  const [statusTo, setStatusTo] = useState<TicketStatus>(ticket.status)
  const transitions = STATUS_TRANSITIONS[ticket.status as TicketStatus] || []

  const { data: tasks = [] } = useQuery<any[]>({
    queryKey: ['ticket-tasks', ticket.id],
    queryFn: () => api.get(`/tickets/${ticket.id}/tasks`).then(r => r.data),
  })

  const save = async () => {
    // ข้อ 5: ปิด ticket ได้ก็ต่อเมื่อ Tasks ทั้งหมดปิดแล้ว
    if (statusTo === 'CLOSED' && tasks.length > 0) {
      const openTasks = tasks.filter((t: any) => !['RESOLVED', 'CLOSED', 'CANCELLED'].includes(t.status))
      if (openTasks.length > 0) {
        alert(`ไม่สามารถปิด Ticket ได้ — ยังมี Task ที่ยังไม่เสร็จ ${openTasks.length} รายการ:\n${openTasks.map((t: any) => `• ${t.title}`).join('\n')}`)
        return
      }
    }
    setSaving(true)
    try {
      await api.put(`/tickets/${ticket.id}/resolution`, { resolution: text })
      if (statusTo !== ticket.status) {
        await api.put(`/tickets/${ticket.id}`, { status: statusTo })
        onStatusChange(statusTo)
      }
      if (addWorkLog) {
        const mins = (parseInt(wlHours || '0') * 60) + parseInt(wlMins || '0')
        if (mins > 0) {
          await api.post(`/tickets/${ticket.id}/worklogs`, { note: wlNote || null, minutes: mins })
        }
      }
      refetch()
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        rows={10}
        placeholder="บันทึกวิธีแก้ไขปัญหา วิธีการที่ใช้ และขั้นตอนที่ดำเนินการ..."
        className="w-full px-4 py-4 text-sm focus:outline-none resize-none border-b border-gray-100"
      />

      {/* Update status */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
        <span className="text-xs font-medium text-gray-500">Update request status to</span>
        <select value={statusTo} onChange={e => setStatusTo(e.target.value as TicketStatus)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value={ticket.status}>{STATUS_CFG[ticket.status as TicketStatus]?.label} (current)</option>
          {transitions.map(s => <option key={s} value={s}>{STATUS_CFG[s]?.label}</option>)}
        </select>
      </div>

      {/* Add Work Log toggle */}
      <div className="px-4 py-3 border-b border-gray-200">
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={addWorkLog} onChange={e => setAddWorkLog(e.target.checked)} className="rounded"/>
          Add Work Log
        </label>
        {addWorkLog && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Time Taken</label>
              <div className="flex gap-2">
                <input type="number" min="0" placeholder="0" value={wlHours} onChange={e => setWlHours(e.target.value)}
                  className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none"/>
                <span className="text-xs text-gray-400 self-center">Hours</span>
                <input type="number" min="0" max="59" placeholder="0" value={wlMins} onChange={e => setWlMins(e.target.value)}
                  className="w-20 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none"/>
                <span className="text-xs text-gray-400 self-center">Minutes</span>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Description</label>
              <input value={wlNote} onChange={e => setWlNote(e.target.value)} placeholder="Work log description..."
                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none"/>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 flex items-center gap-3">
        <button onClick={save} disabled={saving}
          className="px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1.5">
          {saving ? 'Saving...' : saved ? <><CheckCircle size={14}/> Saved</> : 'Save'}
        </button>
        <button onClick={() => { setText(ticket.resolution || ''); setSaved(false) }}
          className="px-4 py-2 border border-gray-200 text-sm rounded-lg hover:bg-gray-50">
          Cancel
        </button>
      </div>
    </div>
  )
}

// ─── Task Status Config ───────────────────────────────────────────────────────

const TASK_STATUSES = ['OPEN','IN_PROGRESS','ONHOLD','ASSIGNED','RESOLVED','CLOSED','CANCELLED'] as const
type TaskStatus = typeof TASK_STATUSES[number]
const TASK_STATUS_CFG: Record<TaskStatus, { label: string; color: string }> = {
  OPEN:        { label: 'Open',        color: 'bg-blue-100 text-blue-700' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-purple-100 text-purple-700' },
  ONHOLD:      { label: 'On Hold',     color: 'bg-amber-100 text-amber-700' },
  ASSIGNED:    { label: 'Assigned',    color: 'bg-cyan-100 text-cyan-700' },
  RESOLVED:    { label: 'Resolved',    color: 'bg-green-100 text-green-700' },
  CLOSED:      { label: 'Closed',      color: 'bg-gray-100 text-gray-600' },
  CANCELLED:   { label: 'Cancelled',   color: 'bg-red-100 text-red-600' },
}
const TASK_PRIORITIES = ['LOW','NORMAL','HIGH','URGENT'] as const
const TASK_PRIORITY_CFG: Record<string, { label: string; color: string }> = {
  LOW:    { label: 'Low',    color: 'text-gray-500' },
  NORMAL: { label: 'Normal', color: 'text-green-600' },
  HIGH:   { label: 'High',   color: 'text-amber-600' },
  URGENT: { label: 'Urgent', color: 'text-red-600' },
}

// ─── Add Task Modal ───────────────────────────────────────────────────────────

function AddTaskModal({ ticketId, onClose, onSaved }: { ticketId: string; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ title: '', description: '', taskType: '', priority: 'NORMAL', assigneeId: '', scheduledStart: '', scheduledEnd: '', estimatedMinutes: '' })
  const [saving, setSaving] = useState(false)
  const { data: assignees = [] } = useQuery<any[]>({ queryKey: ['users-assignable'], queryFn: () => api.get('/users/assignable').then(r => r.data) })
  const inp = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  const save = async () => {
    if (!form.title.trim()) return alert('กรุณาระบุชื่องาน')
    setSaving(true)
    try {
      await api.post(`/tickets/${ticketId}/tasks`, { ...form, assigneeId: form.assigneeId || null, estimatedMinutes: form.estimatedMinutes || null })
      onSaved(); onClose()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">Add New Task</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg text-gray-400"><XCircle size={17}/></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} className={inp} placeholder="Task title..."/>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={3} className={inp} placeholder="Task description..."/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Task Type</label>
              <select value={form.taskType} onChange={e => setForm(f => ({...f, taskType: e.target.value}))} className={inp}>
                <option value="">— None —</option>
                <option value="Troubleshooting">Troubleshooting</option>
                <option value="Implementation">Implementation</option>
                <option value="Investigation">Investigation</option>
                <option value="Testing">Testing</option>
                <option value="Documentation">Documentation</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className={inp}>
                {TASK_PRIORITIES.map(p => <option key={p} value={p}>{TASK_PRIORITY_CFG[p].label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Owner</label>
            <select value={form.assigneeId} onChange={e => setForm(f => ({...f, assigneeId: e.target.value}))} className={inp}>
              <option value="">— Not Assigned —</option>
              {assignees.map((u: any) => <option key={u.id} value={u.id}>{u.displayName || u.username}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Scheduled Start</label>
              <input type="datetime-local" value={form.scheduledStart} onChange={e => setForm(f => ({...f, scheduledStart: e.target.value}))} className={inp}/>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Scheduled End</label>
              <input type="datetime-local" value={form.scheduledEnd} onChange={e => setForm(f => ({...f, scheduledEnd: e.target.value}))} className={inp}/>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Estimated Effort (minutes)</label>
            <input type="number" min="0" value={form.estimatedMinutes} onChange={e => setForm(f => ({...f, estimatedMinutes: e.target.value}))} className={inp} placeholder="0"/>
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving} className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Task'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Task Detail Panel ────────────────────────────────────────────────────────

function TaskDetailPanel({ task, ticketId, onClose, onSaved }: { task: any; ticketId: string; onClose: () => void; onSaved: () => void }) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    title:           task.title,
    description:     task.description || '',
    taskType:        task.taskType || '',
    status:          task.status || 'OPEN',
    priority:        task.priority || 'NORMAL',
    percentComplete: String(task.percentComplete || 0),
    assigneeId:      task.assigneeId || '',
    scheduledStart:  task.scheduledStart ? new Date(task.scheduledStart).toISOString().slice(0,16) : '',
    scheduledEnd:    task.scheduledEnd   ? new Date(task.scheduledEnd).toISOString().slice(0,16) : '',
    actualStart:     task.actualStart    ? new Date(task.actualStart).toISOString().slice(0,16) : '',
    actualEnd:       task.actualEnd      ? new Date(task.actualEnd).toISOString().slice(0,16) : '',
    dueDate:         task.dueDate        ? new Date(task.dueDate).toISOString().slice(0,16) : '',
    estimatedMinutes:String(task.estimatedMinutes || ''),
    additionalCost:  String(task.additionalCost || ''),
  })
  const [saving, setSaving] = useState(false)
  const [detailTab, setDetailTab] = useState<'details'|'worklogs'|'history'>('details')
  const { data: assignees = [] } = useQuery<any[]>({ queryKey: ['users-assignable'], queryFn: () => api.get('/users/assignable').then(r => r.data) })
  const inp = 'w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white'

  const save = async () => {
    setSaving(true)
    try {
      await api.put(`/tickets/${ticketId}/tasks/${task.id}`, {
        ...form,
        assigneeId:      form.assigneeId || null,
        percentComplete: Number(form.percentComplete),
        estimatedMinutes:form.estimatedMinutes ? Number(form.estimatedMinutes) : null,
        additionalCost:  form.additionalCost   ? parseFloat(form.additionalCost) : null,
      })
      setEditing(false)
      onSaved()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  const statusCfg = TASK_STATUS_CFG[form.status as TaskStatus] || TASK_STATUS_CFG.OPEN
  const prioCfg   = TASK_PRIORITY_CFG[form.priority] || TASK_PRIORITY_CFG.NORMAL

  const PropCell = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <>
      <td className="py-1.5 pr-3 text-xs text-gray-500 text-right whitespace-nowrap w-36 border-r border-gray-200 pr-3">{label}</td>
      <td className="py-1.5 pl-3 text-sm text-gray-800">{children}</td>
    </>
  )

  return (
    <div className="border-t-2 border-blue-200 bg-white">
      {/* Task header */}
      <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <ListTodo size={18} className="text-gray-500"/>
          </div>
          <div className="flex-1 min-w-0">
            {editing ? (
              <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))}
                className="text-base font-semibold border-b border-blue-400 focus:outline-none w-full pb-0.5"/>
            ) : (
              <h3 className="text-base font-semibold text-gray-900">{task.title}</h3>
            )}
            <div className="flex items-center gap-2 mt-1">
              {!editing && (
                <button onClick={() => setEditing(true)} className="text-blue-500 hover:text-blue-700 text-xs flex items-center gap-0.5">
                  <Edit3 size={11}/> Edit
                </button>
              )}
              {editing && (
                <>
                  <button onClick={save} disabled={saving}
                    className="text-xs px-2 py-0.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                    {saving ? '...' : 'Save'}
                  </button>
                  <button onClick={() => setEditing(false)} className="text-xs px-2 py-0.5 border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3 flex-shrink-0">
          <div className="text-center border border-gray-200 rounded-lg px-4 py-2 min-w-24">
            <p className="text-xs text-gray-400 mb-0.5">Due Date</p>
            <p className="text-sm font-medium text-gray-700">
              {task.dueDate ? new Date(task.dueDate).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : 'N/A'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><XCircle size={16}/></button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex border-b border-gray-200 px-5">
        {(['details','worklogs','history'] as const).map(t => (
          <button key={t} onClick={() => setDetailTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${detailTab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t === 'worklogs' ? 'Work Log' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Details sub-tab */}
      {detailTab === 'details' && (
        <div className="p-5 space-y-4">
          {/* Description */}
          {(editing || task.description) && (
            <div className="border border-gray-200 rounded-lg p-3">
              {editing ? (
                <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                  rows={3} placeholder="Task description..." className="w-full text-sm focus:outline-none resize-none"/>
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-line">{task.description}</p>
              )}
            </div>
          )}

          {/* Properties 2-column table */}
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <tbody className="divide-y divide-gray-100">
              <tr>
                <PropCell label="Priority">
                  {editing ? (
                    <select value={form.priority} onChange={e => setForm(f => ({...f, priority: e.target.value}))} className={inp}>
                      {TASK_PRIORITIES.map(p => <option key={p} value={p}>{TASK_PRIORITY_CFG[p].label}</option>)}
                    </select>
                  ) : <span className={`font-medium ${prioCfg.color}`}>{prioCfg.label}</span>}
                </PropCell>
                <PropCell label="Status">
                  {editing ? (
                    <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))} className={inp}>
                      {TASK_STATUSES.map(s => <option key={s} value={s}>{TASK_STATUS_CFG[s].label}</option>)}
                    </select>
                  ) : <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusCfg.color}`}>{statusCfg.label}</span>}
                </PropCell>
              </tr>
              <tr>
                <PropCell label="Task Type">
                  {editing ? (
                    <select value={form.taskType} onChange={e => setForm(f => ({...f, taskType: e.target.value}))} className={inp}>
                      <option value="">— None —</option>
                      {['Troubleshooting','Implementation','Investigation','Testing','Documentation','Other'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : <span className="text-gray-700">{task.taskType || '—'}</span>}
                </PropCell>
                <PropCell label="Owner">
                  {editing ? (
                    <select value={form.assigneeId} onChange={e => setForm(f => ({...f, assigneeId: e.target.value}))} className={inp}>
                      <option value="">— Not Assigned —</option>
                      {assignees.map((u: any) => <option key={u.id} value={u.id}>{u.displayName || u.username}</option>)}
                    </select>
                  ) : task.assignee ? (
                    <div className="flex items-center gap-1.5">
                      <Avatar name={task.assignee.displayName || task.assignee.username} size="sm"/>
                      <span>{task.assignee.displayName || task.assignee.username}</span>
                    </div>
                  ) : <span className="text-gray-400">—</span>}
                </PropCell>
              </tr>
              <tr>
                <PropCell label="% of Completion">
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" max="100" value={form.percentComplete}
                        onChange={e => setForm(f => ({...f, percentComplete: e.target.value}))} className={inp + ' w-20'}/>
                      <span className="text-gray-400 text-xs">%</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-1.5 max-w-32">
                        <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${task.percentComplete || 0}%` }}/>
                      </div>
                      <span className="text-xs text-gray-600">{task.percentComplete || 0}%</span>
                    </div>
                  )}
                </PropCell>
                <PropCell label="Actual Start">
                  {editing ? (
                    <input type="datetime-local" value={form.actualStart} onChange={e => setForm(f => ({...f, actualStart: e.target.value}))} className={inp}/>
                  ) : <span className="text-gray-700">{task.actualStart ? fmtDate(task.actualStart) : '—'}</span>}
                </PropCell>
              </tr>
              <tr>
                <PropCell label="Scheduled Start">
                  {editing ? (
                    <input type="datetime-local" value={form.scheduledStart} onChange={e => setForm(f => ({...f, scheduledStart: e.target.value}))} className={inp}/>
                  ) : <span className="text-gray-700">{task.scheduledStart ? fmtDate(task.scheduledStart) : '—'}</span>}
                </PropCell>
                <PropCell label="Actual End">
                  {editing ? (
                    <input type="datetime-local" value={form.actualEnd} onChange={e => setForm(f => ({...f, actualEnd: e.target.value}))} className={inp}/>
                  ) : <span className="text-gray-700">{task.actualEnd ? fmtDate(task.actualEnd) : '—'}</span>}
                </PropCell>
              </tr>
              <tr>
                <PropCell label="Scheduled End">
                  {editing ? (
                    <input type="datetime-local" value={form.scheduledEnd} onChange={e => setForm(f => ({...f, scheduledEnd: e.target.value}))} className={inp}/>
                  ) : <span className="text-gray-700">{task.scheduledEnd ? fmtDate(task.scheduledEnd) : '—'}</span>}
                </PropCell>
                <PropCell label="Additional Cost (THB)">
                  {editing ? (
                    <input type="number" min="0" step="0.01" value={form.additionalCost}
                      onChange={e => setForm(f => ({...f, additionalCost: e.target.value}))} className={inp} placeholder="0.00"/>
                  ) : <span className="text-gray-700">{task.additionalCost != null ? Number(task.additionalCost).toFixed(2) : '0.00'}</span>}
                </PropCell>
              </tr>
              <tr>
                <PropCell label="Estimated Effort">
                  {editing ? (
                    <div className="flex items-center gap-1">
                      <input type="number" min="0" value={form.estimatedMinutes}
                        onChange={e => setForm(f => ({...f, estimatedMinutes: e.target.value}))} className={inp + ' w-20'} placeholder="0"/>
                      <span className="text-xs text-gray-400">mins</span>
                    </div>
                  ) : <span className="text-gray-700">{task.estimatedMinutes ? fmtMinutes(task.estimatedMinutes) : '—'}</span>}
                </PropCell>
                <PropCell label="Created Time">
                  <span className="text-gray-700">{fmtDate(task.createdAt)}</span>
                </PropCell>
              </tr>
            </tbody>
          </table>

          {/* Attachments */}
          <TaskAttachmentsSection task={task} ticketId={ticketId} onSaved={onSaved}/>
        </div>
      )}

      {/* Work Log sub-tab */}
      {detailTab === 'worklogs' && (
        <TaskWorkLogTab task={task} ticketId={ticketId}/>
      )}

      {/* History sub-tab */}
      {detailTab === 'history' && (
        <div className="p-5 text-sm text-gray-400 text-center py-8">ยังไม่มีประวัติสำหรับ task นี้</div>
      )}
    </div>
  )
}

// ─── Ticket Attachments Section ──────────────────────────────────────────────

function TicketAttachmentsSection({ ticketId, initial, readOnly, onSaved }: { ticketId: string; initial: any[]; readOnly?: boolean; onSaved: () => void }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const { data: attachments = [] } = useQuery<any[]>({
    queryKey: ['ticket-attachments', ticketId],
    queryFn: () => api.get(`/tickets/${ticketId}/attachments`).then(r => r.data),
    initialData: initial,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ticket-attachments', ticketId] })
    onSaved()
  }

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    const tooBig = Array.from(files).find(f => f.size > 20 * 1024 * 1024)
    if (tooBig) { alert(`ไฟล์ "${tooBig.name}" ใหญ่เกิน 20 MB`); return }
    setUploading(true)
    try {
      const fd = new FormData()
      Array.from(files).forEach(f => fd.append('files', f))
      await api.post(`/tickets/${ticketId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      invalidate()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setUploading(false) }
  }

  const remove = async (attId: string) => {
    if (!confirm('ลบไฟล์นี้?')) return
    await api.delete(`/tickets/${ticketId}/attachments/${attId}`)
    invalidate()
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
          <Paperclip size={13}/> Attachments {attachments.length > 0 && `(${attachments.length})`}
        </p>
        {!readOnly && (
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50">
            <Plus size={12}/> {uploading ? 'กำลังอัปโหลด...' : 'แนบไฟล์'}
          </button>
        )}
        <input ref={fileRef} type="file" multiple className="hidden"
          accept=".pdf,.xls,.xlsx,.csv,.doc,.docx,.ppt,.pptx,.txt,.zip,.rar,.7z,image/*"
          onChange={e => { upload(e.target.files); e.target.value = '' }}/>
      </div>
      <div className="p-4">
        {attachments.length === 0 ? (
          <div className={`text-center py-4 ${readOnly ? '' : 'cursor-pointer'}`} onClick={() => !readOnly && fileRef.current?.click()}>
            <Paperclip size={20} className="mx-auto mb-1 text-gray-300"/>
            <p className="text-xs text-gray-400">
              {readOnly ? 'ไม่มีไฟล์แนบ' : 'คลิกเพื่อแนบไฟล์ — PDF, Excel, Word, รูปภาพ ฯลฯ [ สูงสุด 20 MB ]'}
            </p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {attachments.map((att: any) => (
              <div key={att.id} className="flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-200 px-2.5 py-1.5 rounded-lg">
                <FileIcon mime={att.mimeType}/>
                <a href={att.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline max-w-40 truncate">{att.originalName}</a>
                {att.size && <span className="text-gray-400">({Math.round(att.size / 1024)}KB)</span>}
                {!readOnly && <button onClick={() => remove(att.id)} className="text-gray-300 hover:text-red-500 ml-0.5"><XCircle size={12}/></button>}
              </div>
            ))}
            {!readOnly && (
              <button onClick={() => fileRef.current?.click()}
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 border border-dashed border-blue-200 px-2.5 py-1.5 rounded-lg">
                <Plus size={11}/> เพิ่มไฟล์
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Task Attachments Section ─────────────────────────────────────────────────

function TaskAttachmentsSection({ task, ticketId, onSaved }: { task: any; ticketId: string; onSaved: () => void }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const { data: attachments = [] } = useQuery<any[]>({
    queryKey: ['task-attachments', task.id],
    queryFn: () => api.get(`/tickets/${ticketId}/tasks/${task.id}/attachments`).then(r => r.data),
    initialData: task.attachments || [],
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['task-attachments', task.id] })

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    setUploading(true)
    try {
      const fd = new FormData()
      Array.from(files).forEach(f => fd.append('files', f))
      await api.post(`/tickets/${ticketId}/tasks/${task.id}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      invalidate(); onSaved()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setUploading(false) }
  }

  const remove = async (attId: string) => {
    if (!confirm('ลบไฟล์นี้?')) return
    await api.delete(`/tickets/${ticketId}/tasks/${task.id}/attachments/${attId}`)
    invalidate(); onSaved()
  }

  return (
    <div className="border border-dashed border-gray-300 rounded-lg p-4 mt-1">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1.5">
          <Paperclip size={13}/> Attachments {attachments.length > 0 && `(${attachments.length})`}
        </p>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50">
          <Plus size={12}/> {uploading ? 'Uploading...' : 'Browse Files'}
        </button>
        <input ref={fileRef} type="file" multiple className="hidden" onChange={e => upload(e.target.files)}/>
      </div>

      {attachments.length === 0 ? (
        <div className="text-center py-4 cursor-pointer" onClick={() => fileRef.current?.click()}>
          <Paperclip size={20} className="mx-auto mb-1 text-gray-300"/>
          <p className="text-xs text-gray-400">Browse Files or Drag files here [ Max size: 20 MB. ]</p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {attachments.map((att: any) => (
            <div key={att.id} className="flex items-center gap-1.5 text-xs bg-gray-50 border border-gray-200 px-2.5 py-1.5 rounded-lg">
              <FileIcon mime={att.mimeType}/>
              <a href={att.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline max-w-40 truncate">{att.originalName}</a>
              {att.size && <span className="text-gray-400">({Math.round(att.size / 1024)}KB)</span>}
              <button onClick={() => remove(att.id)} className="text-gray-300 hover:text-red-500 ml-0.5"><XCircle size={12}/></button>
            </div>
          ))}
          <button onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 border border-dashed border-blue-200 px-2.5 py-1.5 rounded-lg">
            <Plus size={11}/> Add more
          </button>
        </div>
      )}
    </div>
  )
}

// ─── Task Work Log Tab ────────────────────────────────────────────────────────

const WORKLOG_TYPES = ['Troubleshooting','Research','Configuration','Testing','Communication','Documentation','Other']

function TaskWorkLogTab({ task, ticketId }: { task: any; ticketId: string }) {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(true)
  const blankForm = { userId: '', worklogType: '', hours: '', minutes: '', startTime: '', endTime: '', ownerCostPerHour: '', otherCharge: '', note: '' }
  const [form, setForm] = useState(blankForm)

  const { data: logs = [], isLoading } = useQuery<any[]>({
    queryKey: ['task-worklogs', task.id],
    queryFn: () => api.get(`/tickets/${ticketId}/tasks/${task.id}/worklogs`).then(r => r.data),
  })
  const { data: assignees = [] } = useQuery<any[]>({ queryKey: ['users-assignable'], queryFn: () => api.get('/users/assignable').then(r => r.data) })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['task-worklogs', task.id] })

  const h = Number(form.hours || 0)
  const m = Number(form.minutes || 0)
  const totalMins = h * 60 + m
  const costPerHour = parseFloat(form.ownerCostPerHour || '0')
  const techCharge = (totalMins / 60) * costPerHour
  const otherCharge = parseFloat(form.otherCharge || '0')
  const totalCharge = techCharge + otherCharge

  const [saving, setSaving] = useState(false)
  const save = async (andNew = false) => {
    if (totalMins <= 0) return alert('กรุณาระบุเวลา')
    setSaving(true)
    try {
      await api.post(`/tickets/${ticketId}/tasks/${task.id}/worklogs`, { ...form, userId: form.userId || null })
      invalidate()
      if (andNew) setForm(blankForm)
      else { setForm(blankForm); setShowForm(false) }
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  const deleteLog = async (id: string) => {
    await api.delete(`/tickets/${ticketId}/tasks/${task.id}/worklogs/${id}`)
    invalidate()
  }

  const inp = 'px-2.5 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white w-full'
  const lbl = 'text-right text-xs text-red-500 font-medium pr-3 py-2 align-middle whitespace-nowrap w-44'

  const totalLogged = logs.reduce((s: number, l: any) => s + l.hours * 60 + l.minutes, 0)

  return (
    <div className="p-5 space-y-4">
      {/* Existing logs */}
      {logs.length > 0 && (
        <div className="space-y-2 mb-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Work Logs ({logs.length})</p>
            <span className="text-xs text-blue-600 font-medium">Total: {fmtMinutes(totalLogged)}</span>
          </div>
          {logs.map((log: any) => (
            <div key={log.id} className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Timer size={14} className="text-blue-600"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-blue-700">{log.hours}h {log.minutes}m</span>
                  <span className="text-xs text-gray-400">by {log.user?.displayName || log.user?.username || 'User'}</span>
                  {log.worklogType && <span className="text-xs bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded">{log.worklogType}</span>}
                  <span className="text-xs text-gray-400 ml-auto">{fmtDate(log.loggedAt)}</span>
                </div>
                {log.note && <p className="text-sm text-gray-600 mt-0.5">{log.note}</p>}
                {(log.ownerCostPerHour || log.otherCharge) && (
                  <div className="text-xs text-gray-400 mt-0.5">
                    {log.ownerCostPerHour && <span>Cost/hr: ฿{log.ownerCostPerHour} </span>}
                    {log.otherCharge && <span>Other: ฿{log.otherCharge}</span>}
                  </div>
                )}
              </div>
              <button onClick={() => deleteLog(log.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0"><Trash2 size={14}/></button>
            </div>
          ))}
        </div>
      )}

      {/* New Work Log form */}
      {!showForm ? (
        <button onClick={() => setShowForm(true)}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium">
          <Plus size={15}/> New Work Log
        </button>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          {/* Form header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Timer size={15} className="text-white"/>
            </div>
            <span className="text-sm font-semibold text-gray-700">New Work Log</span>
          </div>

          {/* Form fields */}
          <div className="p-4">
            <table className="w-full">
              <tbody>
                <tr>
                  <td className={lbl}>Owner</td>
                  <td className="py-1.5 pr-8">
                    <select value={form.userId} onChange={e => setForm(f => ({...f, userId: e.target.value}))} className={inp}>
                      <option value="">— Current User —</option>
                      {assignees.map((u: any) => <option key={u.id} value={u.id}>{u.displayName || u.username}</option>)}
                    </select>
                  </td>
                  <td className={lbl}>Start Time</td>
                  <td className="py-1.5">
                    <input type="datetime-local" value={form.startTime} onChange={e => setForm(f => ({...f, startTime: e.target.value}))} className={inp}/>
                  </td>
                </tr>
                <tr>
                  <td className={lbl}>Time Taken To Resolve</td>
                  <td className="py-1.5 pr-8">
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" placeholder="0" value={form.hours} onChange={e => setForm(f => ({...f, hours: e.target.value}))}
                        className="w-20 px-2.5 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"/>
                      <span className="text-sm text-gray-500">Hours</span>
                      <input type="number" min="0" max="59" placeholder="0" value={form.minutes} onChange={e => setForm(f => ({...f, minutes: e.target.value}))}
                        className="w-20 px-2.5 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-center"/>
                      <span className="text-sm text-gray-500">Minutes</span>
                    </div>
                  </td>
                  <td className={lbl}>End Time</td>
                  <td className="py-1.5">
                    <input type="datetime-local" value={form.endTime} onChange={e => setForm(f => ({...f, endTime: e.target.value}))} className={inp}/>
                  </td>
                </tr>
                <tr>
                  <td className={lbl}>Other Charge (THB)</td>
                  <td className="py-1.5 pr-8">
                    <input type="number" min="0" step="0.01" placeholder="0" value={form.otherCharge} onChange={e => setForm(f => ({...f, otherCharge: e.target.value}))} className={inp}/>
                  </td>
                  <td className={lbl}>Owner's Cost per hour (THB)</td>
                  <td className="py-1.5">
                    <input type="number" min="0" step="0.01" placeholder="0" value={form.ownerCostPerHour} onChange={e => setForm(f => ({...f, ownerCostPerHour: e.target.value}))} className={inp}/>
                  </td>
                </tr>
                <tr>
                  <td className={lbl}>Total Charge (THB)</td>
                  <td className="py-1.5 pr-8">
                    <div className="px-2.5 py-2 border border-gray-100 rounded text-sm bg-gray-50 text-gray-700">{totalCharge.toFixed(2)}</div>
                    <p className="text-xs text-gray-400 mt-0.5">(Total Charge = Tech Charge + Other Charge)</p>
                  </td>
                  <td className={lbl}>Tech Charge (THB)</td>
                  <td className="py-1.5">
                    <div className="px-2.5 py-2 border border-gray-100 rounded text-sm bg-gray-50 text-gray-700">{techCharge.toFixed(2)}</div>
                    <p className="text-xs text-gray-400 mt-0.5">( Tech Charge = Time Taken × Owner's Cost per hour )</p>
                  </td>
                </tr>
                <tr>
                  <td className={lbl}>Worklog Type</td>
                  <td className="py-1.5 pr-8" colSpan={3}>
                    <select value={form.worklogType} onChange={e => setForm(f => ({...f, worklogType: e.target.value}))} className={inp + ' max-w-xs'}>
                      <option value="">------ None ------</option>
                      {WORKLOG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                </tr>
                <tr>
                  <td className={lbl}>Description</td>
                  <td className="py-1.5" colSpan={3}>
                    <textarea value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))}
                      rows={5} placeholder="Work log description..."
                      className="w-full px-2.5 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"/>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-t border-gray-200">
            <button onClick={() => save(false)} disabled={saving}
              className="px-5 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button onClick={() => save(true)} disabled={saving}
              className="px-5 py-2 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50">
              Save and Add New
            </button>
            <button onClick={() => { setShowForm(false); setForm(blankForm) }}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tab: Approval ──────────────────────────────────────────────────────────

const APPROVAL_STATUS_CFG: Record<string, { label: string; color: string }> = {
  PENDING:  { label: 'รอพิจารณา', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  APPROVED: { label: 'อนุมัติ',    color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  REJECTED: { label: 'ไม่อนุมัติ', color: 'bg-red-50 text-red-700 border-red-200' },
}

function ApprovalTab({ ticketId, members, isAdmin, currentUserId }: { ticketId: string; members: any[]; isAdmin: boolean; currentUserId?: string }) {
  const qc = useQueryClient()
  const [pickerId, setPickerId] = useState('')
  const [adding, setAdding] = useState(false)

  const { data: approvals = [], isLoading } = useQuery<any[]>({
    queryKey: ['ticket-approvals', ticketId],
    queryFn: () => api.get(`/tickets/${ticketId}/approvals`).then(r => r.data),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ticket-approvals', ticketId] })
    qc.invalidateQueries({ queryKey: ['ticket', ticketId] })
  }

  const addApprover = async () => {
    if (!pickerId) return
    setAdding(true)
    try {
      await api.post(`/tickets/${ticketId}/approvals`, { approverId: pickerId })
      setPickerId('')
      invalidate()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setAdding(false) }
  }

  const removeApprover = async (approvalId: string) => {
    if (!confirm('นำผู้อนุมัติรายนี้ออกจากรายการหรือไม่?')) return
    await api.delete(`/tickets/${ticketId}/approvals/${approvalId}`)
    invalidate()
  }

  const availableMembers = members.filter((m: any) => !approvals.some((a: any) => a.approverId === m.id))

  const overall = approvals.length === 0 ? null
    : approvals.some((a: any) => a.status === 'REJECTED') ? 'REJECTED'
    : approvals.every((a: any) => a.status === 'APPROVED') ? 'APPROVED'
    : 'PENDING'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        {overall ? (
          <div className={`flex-1 flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium ${APPROVAL_STATUS_CFG[overall].color}`}>
            <UserCheck size={15}/> สถานะการอนุมัติโดยรวม: {APPROVAL_STATUS_CFG[overall].label}
          </div>
        ) : <div className="flex-1"/>}
        <button onClick={() => window.open(`/tickets/${ticketId}/approvals/print`, '_blank')}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 flex-shrink-0">
          <Printer size={14}/> พิมพ์ใบขออนุมัติ
        </button>
      </div>

      {isAdmin && (
        <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm flex gap-2 items-end">
          <div className="flex-1">
            <UserSearchSelect value={pickerId} onChange={setPickerId} placeholder="เลือกผู้ที่ต้องอนุมัติคำขอนี้..." members={availableMembers} label="เพิ่มผู้อนุมัติ"/>
          </div>
          <button onClick={addApprover} disabled={adding || !pickerId}
            className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40">
            <Plus size={15}/> เพิ่ม
          </button>
        </div>
      )}

      <div className="space-y-2">
        {isLoading ? (
          <p className="text-sm text-gray-400 text-center py-4">กำลังโหลด...</p>
        ) : approvals.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">
            ยังไม่มีผู้อนุมัติสำหรับคำขอนี้{isAdmin ? ' — เลือกผู้ใช้ด้านบนเพื่อกำหนดผู้อนุมัติ' : ''}
          </div>
        ) : approvals.map((a: any) => (
          <ApprovalRow key={a.id} ticketId={ticketId} approval={a} isAdmin={isAdmin} currentUserId={currentUserId}
            onRemove={() => removeApprover(a.id)} invalidate={invalidate}/>
        ))}
      </div>
    </div>
  )
}

// Single approver's row: quick digital approve/reject, plus a "print & sign" path
// (draw online or upload a scanned photo of the paper) mirroring Work Order signatures.
function ApprovalRow({ ticketId, approval: a, isAdmin, currentUserId, onRemove, invalidate }: {
  ticketId: string; approval: any; isAdmin: boolean; currentUserId?: string; onRemove: () => void; invalidate: () => void
}) {
  const [mode, setMode] = useState<'idle' | 'reject' | 'sign'>('idle')
  const [rejectComment, setRejectComment] = useState('')
  const [signMode, setSignMode] = useState<'ONLINE' | 'OFFLINE_SCAN'>('ONLINE')
  const [signedByName, setSignedByName] = useState('')
  const [signing, setSigning] = useState(false)
  const padRef = useRef<SignaturePadHandle>(null)
  const scanRef = useRef<HTMLInputElement>(null)

  const cfg = APPROVAL_STATUS_CFG[a.status]
  const canRespond = a.status === 'PENDING' && a.approverId === currentUserId

  const quickApprove = async () => {
    try {
      await api.put(`/tickets/${ticketId}/approvals/${a.id}`, { status: 'APPROVED' })
      invalidate()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
  }

  const confirmReject = async () => {
    try {
      await api.put(`/tickets/${ticketId}/approvals/${a.id}`, { status: 'REJECTED', comment: rejectComment.trim() || undefined })
      setMode('idle'); setRejectComment('')
      invalidate()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
  }

  const uploadSignature = async (file: Blob, source: 'ONLINE' | 'OFFLINE_SCAN') => {
    setSigning(true)
    try {
      const fd = new FormData()
      fd.append('file', file, source === 'ONLINE' ? 'signature.png' : (file as File).name || 'signature.jpg')
      fd.append('source', source)
      if (signedByName) fd.append('signedByName', signedByName)
      await api.post(`/tickets/${ticketId}/approvals/${a.id}/signature`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setMode('idle')
      invalidate()
    } catch (e: any) { alert(e.response?.data?.message || 'บันทึกลายเซ็นไม่สำเร็จ') }
    finally { setSigning(false) }
  }

  const saveDrawnSignature = async () => {
    if (!padRef.current || padRef.current.isEmpty()) { alert('กรุณาเซ็นชื่อในช่องก่อนบันทึก'); return }
    const blob = await padRef.current.toBlob()
    if (blob) await uploadSignature(blob, 'ONLINE')
  }

  const uploadScannedSignature = async (files: FileList | null) => {
    const file = files?.[0]
    if (file) await uploadSignature(file, 'OFFLINE_SCAN')
  }

  const deleteSignature = async () => {
    if (!confirm('ลบลายเซ็นนี้และย้อนสถานะเป็นรอพิจารณาหรือไม่?')) return
    await api.delete(`/tickets/${ticketId}/approvals/${a.id}/signature`)
    invalidate()
  }

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <Avatar name={a.approver?.displayName || a.approver?.username} size="sm"/>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{a.approver?.displayName || a.approver?.username}</p>
          {a.comment && <p className="text-xs text-gray-500 mt-0.5">{a.comment}</p>}
          {a.respondedAt && <p className="text-[11px] text-gray-400 mt-0.5">ตอบกลับเมื่อ {fmtDate(a.respondedAt)}</p>}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${cfg.color}`}>{cfg.label}</span>
        {isAdmin && (
          <button onClick={onRemove} className="text-gray-300 hover:text-red-500 flex-shrink-0">
            <Trash2 size={15}/>
          </button>
        )}
      </div>

      {a.signatureUrl && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-start gap-3">
          <img src={a.signatureUrl} alt="signature" className="border border-gray-200 rounded-lg bg-white max-h-24 object-contain"/>
          <div className="flex-1">
            <p className="text-xs text-gray-600">
              เซ็นโดย {a.signedByName || a.approver?.displayName || a.approver?.username}
              <span className="ml-1.5 text-gray-400">({a.signatureSource === 'OFFLINE_SCAN' ? 'สแกนเอกสาร' : 'เซ็นออนไลน์'})</span>
            </p>
            {isAdmin && <button onClick={deleteSignature} className="text-xs text-red-500 hover:underline mt-1">ลบลายเซ็น</button>}
          </div>
        </div>
      )}

      {canRespond && !a.signatureUrl && mode === 'idle' && (
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          <button onClick={quickApprove}
            className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700">
            <CheckCircle size={13}/> อนุมัติ
          </button>
          <button onClick={() => setMode('sign')}
            className="flex items-center gap-1 px-3 py-1.5 border border-blue-200 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-50">
            <Printer size={13}/> พิมพ์ / เซ็นเอกสาร
          </button>
          <button onClick={() => setMode('reject')}
            className="flex items-center gap-1 px-3 py-1.5 border border-red-200 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50">
            <XCircle size={13}/> ไม่อนุมัติ
          </button>
        </div>
      )}

      {canRespond && mode === 'reject' && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          <textarea
            value={rejectComment}
            onChange={e => setRejectComment(e.target.value)}
            placeholder="เหตุผลที่ไม่อนุมัติ (ถ้ามี)..."
            rows={2}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          />
          <div className="flex gap-2">
            <button onClick={confirmReject}
              className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700">
              ยืนยันไม่อนุมัติ
            </button>
            <button onClick={() => { setMode('idle'); setRejectComment('') }}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">
              ยกเลิก
            </button>
          </div>
        </div>
      )}

      {canRespond && mode === 'sign' && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          <p className="text-xs text-gray-400">
            พิมพ์เอกสารจากปุ่ม "พิมพ์ใบขออนุมัติ" ด้านบนเพื่อนำไปเซ็นด้วยมือ แล้วถ่ายรูป/สแกนกลับมาอัปโหลดที่นี่ หรือเซ็นออนไลน์ในช่องด้านล่างได้ทันที
          </p>
          <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
            <button onClick={() => setSignMode('ONLINE')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${signMode === 'ONLINE' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>เซ็นออนไลน์</button>
            <button onClick={() => setSignMode('OFFLINE_SCAN')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${signMode === 'OFFLINE_SCAN' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>อัปโหลดรูปจากกระดาษ</button>
          </div>

          <input value={signedByName} onChange={e => setSignedByName(e.target.value)} placeholder="ชื่อผู้ลงนาม (ถ้าต่างจากชื่อผู้อนุมัติ)"
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>

          {signMode === 'ONLINE' ? (
            <>
              <SignaturePad ref={padRef} height={160}/>
              <div className="flex items-center justify-end gap-2">
                <button onClick={() => padRef.current?.clear()} className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">ล้าง</button>
                <button onClick={() => setMode('idle')} className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">ยกเลิก</button>
                <button onClick={saveDrawnSignature} disabled={signing} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                  {signing ? 'กำลังบันทึก...' : 'บันทึกลายเซ็น'}
                </button>
              </div>
            </>
          ) : (
            <>
              <button onClick={() => scanRef.current?.click()} disabled={signing}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl py-6 text-sm text-gray-500 hover:border-blue-300 hover:bg-blue-50 disabled:opacity-50">
                {signing ? <RefreshCw size={16} className="animate-spin"/> : <Upload size={16}/>}
                {signing ? 'กำลังอัปโหลด...' : 'เลือกรูปลายเซ็น (JPG/PNG)'}
              </button>
              <input ref={scanRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                onChange={e => uploadScannedSignature(e.target.files)}/>
              <div className="flex justify-end">
                <button onClick={() => setMode('idle')} className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">ยกเลิก</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Tab: Tasks ───────────────────────────────────────────────────────────────

function TasksTab({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient()
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(10)

  const { data: tasks = [], isLoading } = useQuery<any[]>({
    queryKey: ['ticket-tasks', ticketId],
    queryFn: () => api.get(`/tickets/${ticketId}/tasks`).then(r => r.data),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ticket-tasks', ticketId] })

  const deleteTask = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm('ลบงานนี้?')) return
    await api.delete(`/tickets/${ticketId}/tasks/${id}`)
    if (selectedTaskId === id) setSelectedTaskId(null)
    invalidate()
  }

  const selectedTask = tasks.find((t: any) => t.id === selectedTaskId)
  const done = tasks.filter((t: any) => ['RESOLVED','CLOSED'].includes(t.status)).length

  return (
    <div className="space-y-0 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Toolbar */}
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
        <select className="text-xs border border-gray-200 rounded px-2 py-1 bg-white text-gray-600 focus:outline-none">
          <option>All Tasks</option>
        </select>
        <button onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700">
          <Plus size={12}/> Add New
        </button>
        <div className="ml-auto flex items-center gap-3 text-xs text-gray-400">
          {tasks.length > 0 && <span>{done}/{tasks.length} completed</span>}
          <span>Show</span>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
            className="border border-gray-200 rounded px-1.5 py-1 bg-white focus:outline-none text-gray-600">
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
          <span>per page</span>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <p className="text-sm text-gray-400 text-center py-10">กำลังโหลด...</p>
      ) : tasks.length === 0 ? (
        <div className="text-center py-12 text-gray-400 text-sm">
          <ListTodo size={32} className="mx-auto mb-2 opacity-20"/>
          ยังไม่มีงาน — คลิก "Add New" เพื่อเพิ่ม
        </div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs text-gray-500 font-medium bg-gray-50/80">
                <th className="px-3 py-2.5 w-8"><input type="checkbox" className="rounded"/></th>
                <th className="px-3 py-2.5 text-left">Title</th>
                <th className="px-3 py-2.5 text-left w-28">Status</th>
                <th className="px-3 py-2.5 text-left w-24">Priority</th>
                <th className="px-3 py-2.5 text-left w-36">Owner</th>
                <th className="px-3 py-2.5 text-left w-36">Scheduled Start</th>
                <th className="px-3 py-2.5 text-left w-36">Scheduled End</th>
                <th className="px-3 py-2.5 text-center w-28">% of completion</th>
                <th className="px-3 py-2.5 text-center w-20">Task Order</th>
                <th className="px-3 py-2.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {tasks.slice(0, pageSize).map((task: any, i: number) => {
                const isSelected = selectedTaskId === task.id
                const sCfg = TASK_STATUS_CFG[task.status as TaskStatus] || TASK_STATUS_CFG.OPEN
                const pCfg = TASK_PRIORITY_CFG[task.priority] || TASK_PRIORITY_CFG.NORMAL
                return (
                  <>
                    <tr key={task.id}
                      onClick={() => setSelectedTaskId(isSelected ? null : task.id)}
                      className={`border-b border-gray-100 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 border-blue-100' : i % 2 === 1 ? 'bg-gray-50/40 hover:bg-gray-50' : 'hover:bg-gray-50'}`}>
                      <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" className="rounded"/>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`font-medium ${task.isDone ? 'line-through text-gray-400' : 'text-blue-600 hover:underline'}`}>{task.title}</span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${sCfg.color}`}>{sCfg.label}</span>
                      </td>
                      <td className={`px-3 py-2.5 text-xs font-medium ${pCfg.color}`}>{pCfg.label}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">
                        {task.assignee ? (task.assignee.displayName || task.assignee.username) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-500">
                        {task.scheduledStart ? fmtDate(task.scheduledStart) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-500">
                        {task.scheduledEnd ? fmtDate(task.scheduledEnd) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5 justify-center">
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${task.percentComplete || 0}%` }}/>
                          </div>
                          <span className="text-xs text-gray-500 w-8">{task.percentComplete || 0}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 text-center">{i + 1}</td>
                      <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                        <button onClick={e => deleteTask(task.id, e)} className="text-gray-300 hover:text-red-500">
                          <Trash2 size={13}/>
                        </button>
                      </td>
                    </tr>
                    {isSelected && (
                      <tr key={task.id + '-detail'}>
                        <td colSpan={10} className="p-0">
                          <TaskDetailPanel
                            task={task}
                            ticketId={ticketId}
                            onClose={() => setSelectedTaskId(null)}
                            onSaved={invalidate}
                          />
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
            </tbody>
          </table>
          {tasks.length > pageSize && (
            <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400 text-right">
              แสดง {Math.min(pageSize, tasks.length)} จาก {tasks.length} รายการ
            </div>
          )}
        </>
      )}

      {showAddModal && <AddTaskModal ticketId={ticketId} onClose={() => setShowAddModal(false)} onSaved={invalidate}/>}
    </div>
  )
}

// ─── Tab: Checklist ───────────────────────────────────────────────────────────

function ChecklistTab({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient()
  const [newContent, setNewContent] = useState('')
  const [adding, setAdding] = useState(false)

  const { data: items = [], isLoading } = useQuery<any[]>({
    queryKey: ['ticket-checklist', ticketId],
    queryFn: () => api.get(`/tickets/${ticketId}/checklist`).then(r => r.data),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['ticket-checklist', ticketId] })

  const addItem = async () => {
    if (!newContent.trim()) return
    setAdding(true)
    try {
      await api.post(`/tickets/${ticketId}/checklist`, { content: newContent.trim() })
      setNewContent('')
      invalidate()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setAdding(false) }
  }

  const toggle = async (item: any) => {
    await api.put(`/tickets/${ticketId}/checklist/${item.id}`, { isDone: !item.isDone })
    invalidate()
  }

  const deleteItem = async (id: string) => {
    await api.delete(`/tickets/${ticketId}/checklist/${id}`)
    invalidate()
  }

  const done = items.filter((i: any) => i.isDone).length

  return (
    <div className="space-y-3">
      {items.length > 0 && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <div className="flex-1 bg-gray-100 rounded-full h-1.5">
            <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${(done / items.length) * 100}%` }}/>
          </div>
          <span>{done}/{items.length}</span>
        </div>
      )}

      <div className="space-y-2">
        {isLoading ? (
          <p className="text-sm text-gray-400 text-center py-4">กำลังโหลด...</p>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">ยังไม่มีรายการ — เพิ่มรายการด้านล่าง</div>
        ) : items.map((item: any) => (
          <div key={item.id} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm flex items-center gap-3">
            <button onClick={() => toggle(item)} className="text-gray-400 hover:text-blue-600 flex-shrink-0">
              {item.isDone ? <CheckSquare size={18} className="text-blue-500"/> : <Square size={18}/>}
            </button>
            <p className={`flex-1 text-sm ${item.isDone ? 'line-through text-gray-400' : 'text-gray-800'}`}>{item.content}</p>
            <button onClick={() => deleteItem(item.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0">
              <Trash2 size={15}/>
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          value={newContent}
          onChange={e => setNewContent(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addItem()}
          placeholder="เพิ่มรายการตรวจสอบ..."
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button onClick={addItem} disabled={adding || !newContent.trim()}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-40">
          <Plus size={15}/> เพิ่ม
        </button>
      </div>
    </div>
  )
}

// ─── Tab: Work Logs ───────────────────────────────────────────────────────────

function WorkLogsTab({ ticketId }: { ticketId: string }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({ note: '', hours: '', minutes: '' })
  const [adding, setAdding] = useState(false)

  // รวม ticket-level + task-level worklogs ทั้งหมด
  const { data: allLogs = [], isLoading } = useQuery<any[]>({
    queryKey: ['ticket-worklogs-all', ticketId],
    queryFn: () => api.get(`/tickets/${ticketId}/worklogs/all`).then(r => r.data),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['ticket-worklogs-all', ticketId] })
    qc.invalidateQueries({ queryKey: ['ticket-worklogs', ticketId] })
  }

  const addLog = async () => {
    const mins = (parseInt(form.hours || '0') * 60) + parseInt(form.minutes || '0')
    if (mins <= 0) return alert('กรุณาระบุเวลา')
    setAdding(true)
    try {
      await api.post(`/tickets/${ticketId}/worklogs`, { note: form.note || null, minutes: mins })
      setForm({ note: '', hours: '', minutes: '' })
      invalidate()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setAdding(false) }
  }

  const deleteTicketLog = async (id: string) => {
    if (!confirm('ลบรายการนี้?')) return
    await api.delete(`/tickets/${ticketId}/worklogs/${id}`)
    invalidate()
  }

  const totalMinutes = allLogs.reduce((s: number, l: any) => s + (l.totalMinutes || l.minutes || 0), 0)

  return (
    <div className="space-y-4">
      {allLogs.length > 0 && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center gap-2">
          <Timer size={16} className="text-blue-500"/>
          <span className="text-sm font-medium text-blue-700">เวลารวมทั้งหมด: {fmtMinutes(totalMinutes)}</span>
        </div>
      )}

      <div className="space-y-2">
        {isLoading ? (
          <p className="text-sm text-gray-400 text-center py-4">กำลังโหลด...</p>
        ) : allLogs.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-sm">ยังไม่มีบันทึกการทำงาน</div>
        ) : allLogs.map((log: any) => (
          <div key={log.id + (log.source || '')} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm flex items-start gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${log.source === 'task' ? 'bg-purple-100' : 'bg-blue-100'}`}>
              <Timer size={15} className={log.source === 'task' ? 'text-purple-600' : 'text-blue-600'}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-sm font-medium ${log.source === 'task' ? 'text-purple-700' : 'text-blue-700'}`}>
                  {fmtMinutes(log.totalMinutes || log.minutes || 0)}
                </span>
                <span className="text-xs text-gray-400">— {log.user?.displayName || log.user?.username || 'ผู้ใช้'}</span>
                {log.source === 'task' && log.taskTitle && (
                  <span className="text-xs bg-purple-50 text-purple-600 border border-purple-100 px-1.5 py-0.5 rounded">
                    Task: {log.taskTitle}
                  </span>
                )}
                {log.worklogType && (
                  <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{log.worklogType}</span>
                )}
              </div>
              {log.note && <p className="text-sm text-gray-600 mt-0.5">{log.note}</p>}
              <p className="text-xs text-gray-400 mt-0.5">{fmtDate(log.loggedAt)}</p>
            </div>
            {log.source !== 'task' && (
              <button onClick={() => deleteTicketLog(log.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0">
                <Trash2 size={15}/>
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-3">
        <p className="text-sm font-medium text-gray-700">บันทึกเวลาทำงาน (Ticket)</p>
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs text-gray-400 mb-1 block">ชั่วโมง</label>
            <input type="number" min="0" value={form.hours} onChange={e => setForm(f => ({...f, hours: e.target.value}))}
              placeholder="0" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-400 mb-1 block">นาที</label>
            <input type="number" min="0" max="59" value={form.minutes} onChange={e => setForm(f => ({...f, minutes: e.target.value}))}
              placeholder="0" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
        </div>
        <input value={form.note} onChange={e => setForm(f => ({...f, note: e.target.value}))}
          placeholder="หมายเหตุ (ไม่บังคับ)..."
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
        <button onClick={addLog} disabled={adding}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
          <Plus size={15}/> {adding ? 'กำลังบันทึก...' : 'บันทึกเวลา'}
        </button>
      </div>
    </div>
  )
}

// ─── Tab: Time Analysis ───────────────────────────────────────────────────────

function TimeAnalysisTab({ ticket }: { ticket: any }) {
  const { data: logs = [] } = useQuery<any[]>({
    queryKey: ['ticket-worklogs', ticket.id],
    queryFn: () => api.get(`/tickets/${ticket.id}/worklogs`).then(r => r.data),
  })

  const totalMinutes = logs.reduce((s: number, l: any) => s + l.minutes, 0)

  const byUser = logs.reduce((acc: Record<string, { name: string; minutes: number }>, l: any) => {
    const uid = l.userId || 'unknown'
    if (!acc[uid]) acc[uid] = { name: l.user?.displayName || l.user?.username || 'ผู้ใช้', minutes: 0 }
    acc[uid].minutes += l.minutes
    return acc
  }, {})

  const byDay = logs.reduce((acc: Record<string, number>, l: any) => {
    const day = new Date(l.loggedAt).toLocaleDateString('th-TH-u-ca-gregory', { day:'2-digit', month:'short', year:'numeric' })
    acc[day] = (acc[day] || 0) + l.minutes
    return acc
  }, {})

  const slaMinutes = ticket.sla?.resolveMinutes || 0
  const slaUsed = slaMinutes > 0 ? Math.min(100, Math.round((totalMinutes / slaMinutes) * 100)) : 0

  const createdAt = ticket.createdAt ? new Date(ticket.createdAt) : null
  const resolvedAt = ticket.resolvedAt ? new Date(ticket.resolvedAt) : null
  const elapsedMinutes = createdAt && resolvedAt
    ? Math.round((resolvedAt.getTime() - createdAt.getTime()) / 60000)
    : createdAt
    ? Math.round((Date.now() - createdAt.getTime()) / 60000)
    : 0

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-400 mb-1">เวลาทำงานรวม</p>
          <p className="text-2xl font-bold text-blue-600">{fmtMinutes(totalMinutes)}</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-gray-400 mb-1">เวลาที่ผ่านไป (Elapsed)</p>
          <p className="text-2xl font-bold text-gray-700">{fmtMinutes(elapsedMinutes)}</p>
        </div>
      </div>

      {slaMinutes > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-gray-700">SLA — {ticket.sla?.name}</p>
            <span className="text-xs text-gray-500">{fmtMinutes(totalMinutes)} / {fmtMinutes(slaMinutes)}</span>
          </div>
          <div className="bg-gray-100 rounded-full h-2">
            <div className={`h-2 rounded-full transition-all ${slaUsed >= 100 ? 'bg-red-500' : slaUsed >= 75 ? 'bg-amber-500' : 'bg-green-500'}`}
              style={{ width: `${slaUsed}%` }}/>
          </div>
          <p className="text-xs text-gray-400 mt-1">{slaUsed}% ของ SLA</p>
        </div>
      )}

      {Object.keys(byUser).length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
          <p className="text-sm font-medium text-gray-700 mb-3">เวลาตามผู้ดำเนินการ</p>
          <div className="space-y-2">
            {Object.values(byUser).sort((a: any, b: any) => b.minutes - a.minutes).map((u: any, i) => (
              <div key={i} className="flex items-center gap-3">
                <Avatar name={u.name} size="sm"/>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs text-gray-700">{u.name}</span>
                    <span className="text-xs font-medium text-gray-600">{fmtMinutes(u.minutes)}</span>
                  </div>
                  <div className="bg-gray-100 rounded-full h-1">
                    <div className="bg-blue-400 h-1 rounded-full" style={{ width: `${totalMinutes ? (u.minutes / totalMinutes) * 100 : 0}%` }}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(byDay).length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
          <p className="text-sm font-medium text-gray-700 mb-3">เวลาตามวัน</p>
          <div className="space-y-2">
            {Object.entries(byDay).map(([day, mins]: [string, any]) => (
              <div key={day} className="flex items-center justify-between text-sm">
                <span className="text-gray-600">{day}</span>
                <span className="font-medium text-gray-700">{fmtMinutes(mins)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {logs.length === 0 && (
        <div className="text-center py-8 text-gray-400 text-sm">ยังไม่มีข้อมูลเวลา — บันทึกเวลาในแท็บ Work Logs</div>
      )}
    </div>
  )
}

// ─── Tab: History ─────────────────────────────────────────────────────────────

const ACTION_ICON: Record<string, { icon: React.ReactNode; color: string }> = {
  CREATE: { icon: <Plus size={13}/>,       color: 'bg-green-100 text-green-600' },
  UPDATE: { icon: <Edit3 size={13}/>,      color: 'bg-blue-100 text-blue-600' },
  COMMENT:{ icon: <Send size={13}/>,       color: 'bg-purple-100 text-purple-600' },
  DELETE: { icon: <Trash2 size={13}/>,     color: 'bg-red-100 text-red-500' },
  CLOSE:  { icon: <XCircle size={13}/>,    color: 'bg-gray-100 text-gray-500' },
  RESOLVE:{ icon: <CheckCircle size={13}/>,color: 'bg-green-100 text-green-600' },
}

function HistoryTab({ ticket }: { ticket: any }) {
  const allLogs: any[] = [...(ticket.logs || [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  const [filter, setFilter] = useState('')

  const filtered = filter
    ? allLogs.filter(l => (l.action + ' ' + (l.detail || '')).toLowerCase().includes(filter.toLowerCase()))
    : allLogs

  // Group by date
  const grouped = filtered.reduce((acc: Record<string, any[]>, l) => {
    const day = new Date(l.createdAt).toLocaleDateString('th-TH-u-ca-gregory', { day:'2-digit', month:'long', year:'numeric' })
    if (!acc[day]) acc[day] = []
    acc[day].push(l)
    return acc
  }, {})

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-2">
        <input value={filter} onChange={e => setFilter(e.target.value)}
          placeholder="Filter history..."
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"/>
        {filter && <button onClick={() => setFilter('')} className="text-gray-400 hover:text-gray-600"><XCircle size={16}/></button>}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">ไม่มีประวัติ</div>
      ) : Object.entries(grouped).map(([day, entries]) => (
        <div key={day} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-xs font-semibold text-gray-500">{day}</span>
          </div>
          <div className="divide-y divide-gray-100">
            {entries.map((l: any) => {
              const cfg = ACTION_ICON[l.action] || { icon: <Clock size={13}/>, color: 'bg-gray-100 text-gray-500' }
              return (
                <div key={l.id} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
                  <span className="text-xs text-gray-400 w-16 flex-shrink-0 pt-0.5">
                    {new Date(l.createdAt).toLocaleTimeString('th-TH', { hour:'2-digit', minute:'2-digit' })}
                  </span>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${cfg.color}`}>
                    {cfg.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-gray-500 uppercase">{l.action}</span>
                    {l.detail && <p className="text-sm text-gray-700 mt-0.5">{l.detail}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

type Tab = 'details' | 'resolution' | 'approval' | 'tasks' | 'checklist' | 'worklogs' | 'timeanalysis' | 'history'

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'details',      label: 'Details',       icon: <AlertCircle size={14}/> },
  { id: 'resolution',   label: 'Resolution',    icon: <CheckCircle size={14}/> },
  { id: 'approval',     label: 'Approval',      icon: <UserCheck size={14}/> },
  { id: 'tasks',        label: 'Tasks',         icon: <ListTodo size={14}/> },
  { id: 'checklist',    label: 'Checklist',     icon: <ClipboardList size={14}/> },
  { id: 'worklogs',     label: 'Work Logs',     icon: <Timer size={14}/> },
  { id: 'timeanalysis', label: 'Time Analysis', icon: <BarChart2 size={14}/> },
  { id: 'history',      label: 'History',       icon: <History size={14}/> },
]

export default function TicketDetailPage() {
  const { id }       = useParams<{ id: string }>()
  const navigate     = useNavigate()
  const qc           = useQueryClient()
  const { user, isAdmin, isSuperAdmin } = useAuth()
  const isViewer     = user?.role === 'VIEWER'
  const visibleTabs  = TABS.filter(t => !isViewer || !['tasks','checklist','worklogs','timeanalysis'].includes(t.id))
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm]   = useState<any>({})
  const [saving, setSaving]       = useState(false)
  const [tab, setTab] = useState<Tab>('details')
  const [hasBreach, setHasBreach] = useState(false)

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => api.get(`/tickets/${id}`).then(r => r.data),
    refetchInterval: 60000,
  })
  const { data: categoryTree = [] } = useQuery<any[]>({ queryKey: ['ticket-categories'],  queryFn: () => api.get('/tickets/categories').then(r => r.data) })
  const { data: assignees = [] }    = useQuery<any[]>({ queryKey: ['users-assignable'],   queryFn: () => api.get('/users/assignable').then(r => r.data) })
  const { data: members = [] }      = useQuery<any[]>({ queryKey: ['users-members'],      queryFn: () => api.get('/users/members').then(r => r.data) })
  const { data: companies = [] }    = useQuery<any[]>({ queryKey: ['companies'],           queryFn: () => api.get('/masterdata/companies').then(r => r.data) })

  const refetch = () => qc.invalidateQueries({ queryKey: ['ticket', id] })

  const startEdit = () => {
    if (!ticket) return
    setEditForm({
      title:         ticket.title         || '',
      description:   ticket.description   || '',
      priority:      ticket.priority      || 'MEDIUM',
      type:          ticket.type          || 'INCIDENT',
      categoryId:    ticket.categoryId    || '',
      subcategoryId: ticket.subcategoryId || '',
      itemId:        ticket.itemId        || '',
      requesterId:   ticket.requesterId   || '',
      assigneeId:    ticket.assigneeId    || '',
      assetId:       ticket.assetId       || '',
      companyId:     ticket.companyId     || '',
      createdAt:     ticket.createdAt ? new Date(ticket.createdAt).toISOString().slice(0,16) : '',
    })
    setIsEditing(true)
    setTab('details')
  }

  const saveEdit = async () => {
    setSaving(true)
    try {
      const payload = { ...editForm }
      if (!isSuperAdmin) delete payload.createdAt
      await api.put(`/tickets/${ticket.id}`, payload)
      setIsEditing(false)
      refetch()
    } catch (e: any) { alert(e.response?.data?.message || e.message) }
    finally { setSaving(false) }
  }

  const cancelEdit = () => setIsEditing(false)

  // Socket.io — join this ticket's room for real-time updates
  useEffect(() => {
    if (!id) return
    const apiBase = (import.meta as any).env?.VITE_API_URL?.replace('/api', '') || ''
    const socket = io(apiBase || window.location.origin, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
    })
    socket.on('connect', () => socket.emit('join:ticket', id))
    socket.on('ticket:updated', () => refetch())
    socket.on('sla:breach', (data: any) => {
      if (data.ticketId === id) { setHasBreach(true); refetch() }
    })
    return () => { socket.emit('leave:ticket', id); socket.disconnect() }
  }, [id])

  const changeStatus = async (newStatus: TicketStatus, comment?: string) => {
    await api.put(`/tickets/${id}`, { status: newStatus })
    if (comment?.trim()) {
      await api.post(`/tickets/${id}/comments`, { content: `[On Hold] ${comment}`, isInternal: true })
    }
    refetch()
    qc.invalidateQueries({ queryKey: ['ticket-stats'] })
  }

  if (isLoading) return <div className="flex items-center justify-center h-64 text-gray-400 text-sm">กำลังโหลด...</div>
  if (!ticket)   return <div className="flex items-center justify-center h-64 text-gray-400 text-sm">ไม่พบ Ticket</div>

  const prioConf  = PRIORITY_CFG[ticket.priority as TicketPriority]
  const statConf  = STATUS_CFG[ticket.status as TicketStatus]
  const typeConf  = TYPE_CFG[ticket.type as TicketType]
  const isClosed  = ticket.status === 'CLOSED' || ticket.status === 'CANCELLED'

  return (
    <div className="max-w-7xl mx-auto flex flex-col h-full">
      {/* ── Sticky top: header + properties bar + tabs ── */}
      <div className="sticky top-0 z-10 bg-gray-50 px-6 pt-5 pb-0 flex-shrink-0">

        {/* Header row */}
        <div className="flex items-start justify-between mb-3 gap-4">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate('/tickets')} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 mt-0.5">
              <ArrowLeft size={18}/>
            </button>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="font-mono text-gray-400 text-sm">#{String(ticket.ticketNumber).padStart(4, '0')}</span>
                <span className={`text-xs px-2 py-0.5 rounded font-medium ${typeConf?.color}`}>{typeConf?.label}</span>
                {(hasBreach || (ticket.slaBreaches && ticket.slaBreaches.length > 0)) && (
                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-medium">
                    <ShieldAlert size={11}/> SLA Breach
                  </span>
                )}
              </div>
              {isEditing ? (
                <input value={editForm.title || ''} onChange={e => setEditForm((f: any) => ({...f, title: e.target.value}))}
                  className="text-lg font-bold text-gray-900 border-b-2 border-blue-400 focus:outline-none bg-transparent w-full pb-0.5"/>
              ) : (
                <h1 className="text-lg font-bold text-gray-900 leading-tight">{ticket.title}</h1>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isEditing ? (
              <>
                <button onClick={saveEdit} disabled={saving}
                  className="flex items-center gap-1 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50">
                  {saving ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
                <button onClick={cancelEdit} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                  ยกเลิก
                </button>
              </>
            ) : (
              <button onClick={startEdit} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                <Edit3 size={14}/> แก้ไข
              </button>
            )}
            <StatusDropdown current={ticket.status} onChange={changeStatus}/>
          </div>
        </div>

        {/* Properties summary bar */}
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm px-5 py-3 mb-3">
          <div className="flex flex-wrap gap-x-7 gap-y-2 divide-x divide-gray-100">
            <PropSummaryItem label="ความสำคัญ">
              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${prioConf?.color}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${prioConf?.dot}`}/>
                {prioConf?.label}
              </span>
            </PropSummaryItem>
            <PropSummaryItem label="SLA">
              {ticket.sla ? (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-sm font-medium text-gray-700">{ticket.sla.name}</span>
                  <ResponseSLABadge responseDueDate={ticket.responseDueDate} hasAssignee={!!ticket.assigneeId}/>
                  <SLABadge dueDate={ticket.dueDate} status={ticket.status}/>
                </div>
              ) : <span className="text-gray-400 text-sm">ไม่มี</span>}
            </PropSummaryItem>
            <PropSummaryItem label="ผู้แจ้ง">
              {ticket.requester ? (
                <div className="flex items-center gap-1.5">
                  <Avatar name={ticket.requester.displayName || ticket.requester.username} size="sm"/>
                  <span className="text-sm text-gray-700">{ticket.requester.displayName || ticket.requester.username}</span>
                </div>
              ) : <span className="text-gray-400 text-sm">—</span>}
            </PropSummaryItem>
            <PropSummaryItem label="ผู้รับผิดชอบ">
              {ticket.assignee ? (
                <div className="flex items-center gap-1.5">
                  <Avatar name={ticket.assignee.displayName || ticket.assignee.username} size="sm"/>
                  <span className="text-sm text-gray-700">{ticket.assignee.displayName || ticket.assignee.username}</span>
                </div>
              ) : <span className="text-gray-400 text-sm flex items-center gap-1"><User size={12}/> ยังไม่ได้มอบหมาย</span>}
            </PropSummaryItem>
            <PropSummaryItem label="Category">
              {ticket.category ? (
                <span className="text-sm text-gray-700 flex items-center gap-1">
                  <Tag size={11}/> {ticket.category.name}{ticket.subcategory ? ` › ${ticket.subcategory.name}` : ''}{ticket.item ? ` › ${ticket.item.name}` : ''}
                </span>
              ) : <span className="text-gray-400 text-sm">—</span>}
            </PropSummaryItem>
            <PropSummaryItem label="บริษัท">
              {ticket.company ? (
                <span className="text-sm text-gray-700 flex items-center gap-1"><Building2 size={11}/> {ticket.company.name}</span>
              ) : <span className="text-gray-400 text-sm">—</span>}
            </PropSummaryItem>
            <PropSummaryItem label="Asset">
              {ticket.asset ? (
                <Link to={`/assets/${ticket.asset.id}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                  <Layers size={11}/> {ticket.asset.assetCode}
                </Link>
              ) : <span className="text-gray-400 text-sm">—</span>}
            </PropSummaryItem>
            <PropSummaryItem label="วันที่สร้าง">
              <span className="text-sm text-gray-600">{fmtDate(ticket.createdAt)}</span>
            </PropSummaryItem>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-0.5 border-b border-gray-200 overflow-x-auto">
          {visibleTabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Scrollable tab content (full width) ── */}
      <div className="flex-1 overflow-y-auto px-6 pb-6 pt-4">
        {tab === 'details'      && <DetailsTab ticket={ticket} isClosed={isClosed} refetch={refetch} isEditing={isEditing} editForm={editForm} setEditForm={setEditForm} categoryTree={categoryTree} assignees={assignees} members={members} companies={companies} isViewer={isViewer} isSuperAdmin={isSuperAdmin}/>}
        {tab === 'resolution'   && <ResolutionTab ticket={ticket} refetch={refetch} onStatusChange={changeStatus}/>}
        {tab === 'approval'     && <ApprovalTab ticketId={ticket.id} members={members} isAdmin={isAdmin} currentUserId={user?.id}/>}
        {tab === 'tasks'        && <TasksTab ticketId={ticket.id}/>}
        {tab === 'checklist'    && <ChecklistTab ticketId={ticket.id}/>}
        {tab === 'worklogs'     && <WorkLogsTab ticketId={ticket.id}/>}
        {tab === 'timeanalysis' && <TimeAnalysisTab ticket={ticket}/>}
        {tab === 'history'      && <HistoryTab ticket={ticket}/>}
      </div>

    </div>
  )
}

function PropSummaryItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="pl-7 first:pl-0">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">{label}</p>
      <div>{children}</div>
    </div>
  )
}
